// Behavior for the sticky filter/sort bar rendered above .product-grid-container.
const FOCUSABLE = 'summary, button:not([disabled]), a[href], input:not([type="hidden"]):not([disabled]), select:not([disabled])';

if (!customElements.get('sticky-filter-bar')) {
  class StickyFilterBar extends HTMLElement {
    observer = null;
    drawerObserver = null;
    returnFocusTo = null;
    topSection = null;
    ticking = false;

    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onScrollOrResize = this.onScrollOrResize.bind(this);
      this.updateStickyOffset = this.updateStickyOffset.bind(this);
      this.onGridChange = this.onGridChange.bind(this);
      this.onDrawerToggle = this.onDrawerToggle.bind(this);
      this.addEventListener('click', this.onClick);
    }

    connectedCallback() {
      if (!this.wrapper) return;

      this.initStickyOffset();

      if (this.activeView) this.setView(this.activeView);

      this.syncSixWide();
      this.observeDrawer();
      this.syncFilterExpanded();

      // The grid gets re-fetched via AJAX on filter/sort changes (section-collection.js swaps #product-grid-{sectionId}'s innerHTML)
      const productGridWrapper = this.gridContainer?.querySelector('.product-grid-wrapper');
      if (!productGridWrapper) return;

      this.observer = new MutationObserver(this.onGridChange);
      this.observer.observe(productGridWrapper, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.drawerObserver?.disconnect();
      window.removeEventListener('scroll', this.onScrollOrResize);
      window.removeEventListener('resize', this.onScrollOrResize);
    }

    get wrapper() {
      return this.closest('.collection-filters-wrapper');
    }

    get gridContainer() {
      return this.closest('.product-grid-container');
    }

    get viewButtons() {
      return this.querySelectorAll('.sticky-filter-bar__view-btn');
    }

    /** @returns {string|null} Active grid view ('1' | '4' | '9'), or null when none is selected. */
    get activeView() {
      return this.querySelector('.sticky-filter-bar__view-btn.is-active')?.dataset.gridView ?? null;
    }

    /** @param {MouseEvent} event */
    onClick(event) {
      if (!this.wrapper) return;

      const viewButton = event.target.closest('.sticky-filter-bar__view-btn');
      if (viewButton) {
        this.onViewClick(viewButton);
        return;
      }

      const action = event.target.closest('[data-sticky-action]')?.dataset.stickyAction;
      if (action === 'filter') this.onFilterClick(event);
      if (action === 'sort') this.onSortClick(event);
    }

    /** @param {HTMLButtonElement} button */
    onViewClick(button) {
      const isActive = button.classList.contains('is-active');

      for (const btn of this.viewButtons) {
        const pressed = btn === button && !isActive;
        btn.classList.toggle('is-active', pressed);
        btn.setAttribute('aria-pressed', String(pressed));
      }

      this.setView(isActive ? null : button.dataset.gridView);
      this.updateSwipers();
    }

    /**
     * Applies the grid view and announces it so other grids (Shop Related) can follow.
     * @param {string|null} view
     */
    setView(view) {
      if (view) {
        this.gridContainer?.setAttribute('data-grid-view', view);
      } else {
        this.gridContainer?.removeAttribute('data-grid-view');
      }

      document.dispatchEvent(new CustomEvent('sticky-filter-bar:view-change', { detail: { view } }));
    }

    onGridChange() {
      this.refreshSwipers();
      this.syncSixWide();
    }

    // Swiper pins each slide to a pixel width, so on a view change it must re-measure before the
    // next paint; deferring it paints a frame of oversized, cropped slides (the layout flick).
    updateSwipers() {
      for (const card of this.gridContainer?.querySelectorAll('product-card') ?? []) {
        card.mediaSwiper?.update();
      }
    }

    // Deferred for grid re-renders: updating inside the MutationObserver callback could re-trigger it.
    refreshSwipers() {
      requestAnimationFrame(() => requestAnimationFrame(() => this.updateSwipers()));
    }

    /**
     * Grid cells the full result set fills: rendered cards and interrupters, plus products on
     * infinite-scroll pages not loaded yet (the size filter can drop cards, so products_count alone is off).
     * @returns {number} NaN when there is no grid.
     */
    get gridCellCount() {
      const grid = this.gridContainer?.querySelector('[data-products-count]');
      if (!grid) return NaN;

      let cells = 0;
      for (const child of grid.children) {
        if (child.matches('product-card, .dynamic-interrupter-block-width')) cells += 1;
        if (child.matches('.dynamic-interrupter-first-half-width, .dynamic-interrupter-last-half-width')) cells += 2;
        if (child.matches('.dynamic-interrupter-full-width')) cells += 4;
      }

      const nextPageLink = this.gridContainer.querySelector('infinite-scroll a[href]');
      if (!nextPageLink) return cells;

      const nextPage = parseInt(new URL(nextPageLink.href, window.location.origin).searchParams.get('page'), 10);
      const unloaded = parseInt(grid.dataset.productsCount, 10) - (nextPage - 1) * parseInt(grid.dataset.pageSize, 10);
      return Number.isFinite(unloaded) ? cells + Math.max(unloaded, 0) : NaN;
    }

    // 6-wide only when the grid's cells fill every row of 6; re-run whenever the grid changes
    // (filters, infinite scroll). Leaving the view while on it drops back to the 4-wide view.
    syncSixWide() {
      const sixWide = this.querySelector('.sticky-filter-bar__view-btn[data-grid-view="9"]');
      const count = this.gridCellCount;
      if (!sixWide || !Number.isFinite(count)) return;

      const fits = count > 0 && count % 6 === 0;
      sixWide.hidden = !fits;

      if (!fits && sixWide.classList.contains('is-active')) {
        this.querySelector('.sticky-filter-bar__view-btn[data-grid-view="4"]')?.click();
      }
    }

    // On collections that render a category carousel (sections/category-carousel.liquid)
    // or the PLP category image tiles (sections/plp-filters-blocks.liquid) above the grid
    initStickyOffset() {
      this.topSection = document.querySelector('.category-carousel, .plp-filters-blocks');
      if (!this.topSection) return;

      this.updateStickyOffset();
      window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
      window.addEventListener('resize', this.onScrollOrResize);
    }

    onScrollOrResize() {
      if (this.ticking) return;

      this.ticking = true;
      requestAnimationFrame(this.updateStickyOffset);
    }

    updateStickyOffset() {
      this.ticking = false;
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const topSectionBottom = this.topSection.getBoundingClientRect().bottom;
      this.style.top = `${Math.max(headerHeight, topSectionBottom)}px`;
    }

    // The same trigger button opens the whole filter+sort panel; component-filters-drawer.liquid's
    // root carries Alpine's `open` state directly (`x-data="{ open: false }"`,
    // `:class="{ 'drawer-active': open }"` on .drawer-main-wrapper).
    get drawerRoot() {
      return this.wrapper.querySelector('.drawer__facet-button-trigger')?.closest('.drawer-main-wrapper') ?? null;
    }

    get drawerState() {
      const drawerRoot = this.drawerRoot;
      if (!drawerRoot) return null;

      const trigger = drawerRoot.querySelector('.drawer__facet-button-trigger');

      return {
        trigger,
        isOpen: drawerRoot.classList.contains('drawer-active'),
        setOpen(open) {
          const data = window.Alpine?.$data(drawerRoot);
          if (data && 'open' in data) {
            data.open = open;
          } else {
            trigger.click();
          }
        },
      };
    }

    ensureDrawerOpen() {
      const state = this.drawerState;
      if (state && !state.isOpen) state.setOpen(true);
    }

    // The drawer also closes from its own controls (overlay, close icon), so watch its open class
    observeDrawer() {
      const drawerRoot = this.drawerRoot;
      if (!drawerRoot) return;

      this.drawerObserver = new MutationObserver(this.onDrawerToggle);
      this.drawerObserver.observe(drawerRoot, { attributes: true, attributeFilter: ['class'] });
    }

    onDrawerToggle() {
      this.syncFilterExpanded();
      if (!this.returnFocusTo || this.drawerState?.isOpen) return;

      // Back to the sticky control that opened it, unless focus already moved elsewhere on the page
      const active = document.activeElement;
      if (!active || active === document.body || this.drawerRoot?.contains(active)) this.returnFocusTo.focus();
      this.returnFocusTo = null;
    }

    /**
     * Moves focus into the opened drawer, to the first visible control of `container`.
     * @param {HTMLElement} opener - Sticky button that gets focus back when the drawer closes
     * @param {HTMLElement|null} [container] - Area to focus in; defaults to the whole drawer panel
     */
    focusDrawer(opener, container) {
      const panel = this.drawerRoot?.querySelector('.drawer__wrapper');
      if (!panel) return;

      this.returnFocusTo = opener;

      // Next frame, once Alpine has applied the open state
      requestAnimationFrame(() => {
        const target = [...(container ?? panel).querySelectorAll(FOCUSABLE)].find((el) => el.getClientRects().length > 0);
        target?.focus({ preventScroll: true });
      });
    }

    syncFilterExpanded() {
      const state = this.drawerState;
      const isOpen = !this.isDesktopSidebarLayout && state ? state.isOpen : !this.wrapper.classList.contains('filters-panel-collapsed');
      this.querySelector('[data-sticky-action="filter"]')?.setAttribute('aria-expanded', String(isOpen));
    }

    // Desktop vertical layout shows .filters-sidebar with plain CSS (section-collection.css);
    get isDesktopSidebarLayout() {
      return window.matchMedia('(min-width: 1201px)').matches && !!this.wrapper.querySelector('.filters-sidebar');
    }

    /** @param {MouseEvent} event */
    onFilterClick(event) {
      event.stopPropagation();

      if (this.isDesktopSidebarLayout) {
        this.wrapper.classList.toggle('filters-panel-collapsed');
        this.syncFilterExpanded();
        return;
      }

      // The panel's own Alpine state is the source of truth here
      const state = this.drawerState;
      const isOpen = state ? state.isOpen : !this.wrapper.classList.contains('filters-panel-collapsed');

      if (isOpen) {
        this.wrapper.classList.add('filters-panel-collapsed');
        state?.setOpen(false);
      } else {
        this.wrapper.classList.remove('filters-panel-collapsed');
        state?.setOpen(true);
        if (state) this.focusDrawer(event.target.closest('[data-sticky-action]'));
      }

      // With a drawer, onDrawerToggle syncs once Alpine applies the change
      if (!state) this.syncFilterExpanded();
    }

    /** @param {MouseEvent} event */
    onSortClick(event) {
      event.stopPropagation();
      this.wrapper.classList.remove('filters-panel-collapsed');

      if (!this.isDesktopSidebarLayout) {
        this.ensureDrawerOpen();
      }

      const sortSection = this.openSortControl();
      if (!this.isDesktopSidebarLayout && this.drawerState) {
        this.focusDrawer(event.target.closest('[data-sticky-action]'), sortSection);
      }

      this.syncFilterExpanded();
    }

    // Vertical/horizontal filter_type: a native <select> (sections/collection.liquid).
    // Drawer filter_type: sort lives in its own Alpine-driven <details> accordion inside
    // component-filters-drawer.liquid (#Details-sort_by-{section_id}-Drawer).
    /** @returns {HTMLElement|null} The drawer sort section it opened, or null for the native select */
    openSortControl() {
      const nativeSelect = this.wrapper.querySelector('select.facet-filters__sort');
      if (nativeSelect && nativeSelect.offsetParent !== null) {
        nativeSelect.focus();
        if (typeof nativeSelect.showPicker === 'function') {
          try {
            nativeSelect.showPicker();
          } catch (error) {
            // Some browsers only allow showPicker() from a direct user gesture on the
            // select itself; focusing it is still a reasonable fallback.
          }
        }
        return null;
      }

      // The hidden sidebar has its own sort section first in the DOM; the drawer layout needs the drawer's
      const scope = (!this.isDesktopSidebarLayout && this.drawerRoot) || this.wrapper;
      const sortSection = scope.querySelector('[id^="Details-sort_by-"]');
      if (!sortSection) return null;

      const data = window.Alpine?.$data(sortSection);
      if (data && 'open' in data) {
        data.open = true;
      } else {
        sortSection.querySelector('summary')?.click();
      }

      return sortSection;
    }
  }

  customElements.define('sticky-filter-bar', StickyFilterBar);
}
